import { useMemo, useState } from "react";
import { directoryCategories, directoryItems, directoryRegions } from "@/mocks/homeData";

const stabilityChip: Record<string, string> = {
  高: "bg-secondary-100 text-secondary-900",
  中: "bg-accent-100 text-accent-900",
  低: "bg-primary-100 text-primary-800",
};

export default function Directory() {
  const [category, setCategory] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return directoryItems.filter((item) => {
      const matchCategory = category === "全部" || item.category === category;
      const matchRegion = region === "全部" || item.region === region;
      const matchKeyword =
        kw === "" ||
        item.name.toLowerCase().includes(kw) ||
        item.desc.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchRegion && matchKeyword;
    });
  }, [category, region, keyword]);

  const reset = () => {
    setCategory("全部");
    setRegion("全部");
    setKeyword("");
  };

  return (
    <section id="directory" className="w-full scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] text-accent-700">DIRECTORY / 资源导航</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              资源渠道导航
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              下面按渠道类型、地区与稳定性整理了常见的获取方向。你可以用筛选和关键词快速定位，
              所有结果都来自本页内容，不会跳转到其他页面。
            </p>
          </div>

          <div className="w-full lg:w-72">
            <label htmlFor="directory-search" className="mb-2 block text-xs text-foreground-500">
              在导航中搜索
            </label>
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                id="directory-search"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="如：正版、网盘、字幕…"
                className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-foreground-500">渠道类型</span>
            {directoryCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  category === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-foreground-500">地区</span>
            {directoryRegions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setRegion(item)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  region === item
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-foreground-500">
            共 <span className="font-semibold text-foreground-800">{filtered.length}</span> 条渠道方向
          </p>
          {(category !== "全部" || region !== "全部" || keyword !== "") && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-xs text-primary-700 transition-colors hover:text-primary-800"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <article
                key={item.name}
                className="flex flex-col rounded-xl border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.name}</h3>
                  <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] ${stabilityChip[item.stability]}`}>
                    稳定性 {item.stability}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  <span className="rounded bg-background-200/70 px-2 py-0.5 text-foreground-600">{item.category}</span>
                  <span className="rounded bg-background-200/70 px-2 py-0.5 text-foreground-600">{item.region}</span>
                </div>

                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-300 px-2.5 py-0.5 text-[11px] text-foreground-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-2-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-700">没有符合条件的渠道方向</p>
            <p className="mt-1 text-xs text-foreground-500">试试减少筛选条件，或更换关键词</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-refresh-line"></i>
              重置全部筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}